import { useState, type FormEvent } from "react";
import Reveal from "@/pages/home/components/Reveal";

const FORM_ADDR = "https://readdy.ai/api/form/db52q0mk6jkc2oct4b7g";

export default function Newsletter() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "submitting") return;
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const str = String(value);
      if (str.trim() === "") return;
      payload.append(key, str);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查连接后重试。");
    }
  };

  return (
    <section id="subscribe" className="py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-8">
        <Reveal>
          <div className="rounded-lg bg-primary-500 px-5 py-10 md:px-12 md:py-14">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-400/60 text-xs font-medium text-background-50">
                  <i className="ri-mail-send-line"></i>
                  更新提醒
                </span>
                <h2 className="mt-4 font-heading text-2xl md:text-3xl font-semibold text-background-50">
                  订阅内容更新
                </h2>
                <p className="mt-3 text-sm md:text-base leading-relaxed text-primary-100">
                  留下邮箱，新增合集与壁纸资源上线时可以第一时间收到提醒。只发送更新通知，不转发、不外借，随时可以取消。
                </p>
              </div>

              <form data-readdy-form id="subscribe-form" onSubmit={onSubmit} className="w-full">
                <div className="flex flex-col sm:flex-row gap-3">
                  <label className="sr-only" htmlFor="subscribe-email">
                    邮箱地址
                  </label>
                  <input
                    id="subscribe-email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="请输入你的邮箱地址"
                    className="flex-1 px-4 py-3 rounded-md bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:ring-2 focus:ring-accent-400"
                  />
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="px-6 py-3 rounded-md bg-accent-500 text-sm font-medium text-foreground-950 hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {status === "submitting" ? "提交中…" : "立即订阅"}
                  </button>
                </div>

                <div className="mt-3 flex flex-col sm:flex-row gap-3">
                  <label className="sr-only" htmlFor="subscribe-topic">
                    感兴趣的主题
                  </label>
                  <select
                    id="subscribe-topic"
                    name="topic"
                    defaultValue=""
                    className="w-full sm:w-48 px-4 py-2.5 rounded-md bg-background-50 text-sm text-foreground-900 outline-none focus:ring-2 focus:ring-accent-400 cursor-pointer"
                  >
                    <option value="">感兴趣的主题（可选）</option>
                    <option value="高清写真">高清写真</option>
                    <option value="日常自拍">日常自拍</option>
                    <option value="室内人像">室内人像</option>
                    <option value="户外旅拍">户外旅拍</option>
                    <option value="壁纸资源">壁纸资源</option>
                  </select>
                  <label className="sr-only" htmlFor="subscribe-note">
                    备注
                  </label>
                  <input
                    id="subscribe-note"
                    name="note"
                    type="text"
                    maxLength={120}
                    placeholder="想优先看到的内容（可选）"
                    className="flex-1 px-4 py-2.5 rounded-md bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:ring-2 focus:ring-accent-400"
                  />
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="form-field-optional-extra"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "success" && (
                  <p className="mt-3 flex items-center gap-2 text-sm text-background-50">
                    <i className="ri-checkbox-circle-line"></i>
                    订阅成功，感谢关注，更新上线时会第一时间通知你。
                  </p>
                )}
                {status === "error" && formError && (
                  <p className="mt-3 flex items-center gap-2 text-sm text-background-50">
                    <i className="ri-error-warning-line"></i>
                    {formError}
                  </p>
                )}
              </form>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}